import { useEffect, useMemo, useState } from "react";
import {
  Building2,
  ChevronDown,
  Copy,
  ExternalLink,
  Eye,
  FileText,
  Landmark,
  MapPin,
  Pencil,
  Save,
  Sparkles,
  TrendingUp,
  X,
  Loader2,
} from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { toast } from "react-toastify";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Chip } from "../ui/chip";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Textarea } from "../ui/textarea";
import {
  Tooltip as UiTooltip,
  TooltipContent as UiTooltipContent,
  TooltipProvider as UiTooltipProvider,
  TooltipTrigger as UiTooltipTrigger,
} from "../ui/tooltip";
import {
  STATE_NAME_MAP,
  STATE_SHORT_MAP,
  getStateCodeFromGSTIN,
} from "../../lib/gst-utils";
import { APOLLO_COMPANY_TEST_PAYLOAD } from "./apolloTestData";
import { dateLabel, money, NA, titleCase } from "./format";
import { MarketData } from "./MarketData";
import { normalizeApolloOrganization } from "./normalizeApolloOrganization";
import {
  SectorIndustryOutlookCard,
  normalizeSectorIndustryOutlook,
  type SectorIndustryOutlookData,
} from "./SectorIndustryOutlookCard";
import { SECTOR_INDUSTRY_OUTLOOK_TEST_PAYLOAD } from "./sectorOutlookTestData";
import type { CompanyProfile } from "./types";

const AI_SPINNER_STYLE: React.CSSProperties = {
  width: "20px",
  height: "20px",
  border: "3px solid #8200DB",
  borderTop: "3px solid #ffffff",
  borderRadius: "50%",
  animation: "spin 1s linear infinite",
};

function isUnavailable(value?: string | null) {
  if (value == null) return true;
  const v = value.trim().toLowerCase();
  return (
    !v ||
    v === "not available" ||
    v === "n/a" ||
    v === "na" ||
    v === "--" ||
    v === "—" ||
    v === "not publicly listed"
  );
}

function ReadField({
  label,
  value,
  hideIfUnavailable = true,
}: {
  label: string;
  value: string;
  hideIfUnavailable?: boolean;
}) {
  if (hideIfUnavailable && isUnavailable(value)) return null;
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-sm font-medium break-words">{value}</dd>
    </div>
  );
}

/** Always-visible field for Apollo structure / parent chain. */
function StructureField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-sm font-medium break-words">{value ?? NA}</dd>
    </div>
  );
}

/** Apollo growth rates are usually fractions (0.12); some payloads send percent (12). */
function asGrowthRate(rate: number | null): number | null {
  if (rate === null || !Number.isFinite(rate)) return null;
  return Math.abs(rate) > 1 ? rate / 100 : rate;
}

type CreditEditable = {
  brandName: string;
  creditRating: string;
  creditRatingAgency: string;
  turnoverYear: string;
  turnover: string;
  networthYear: string;
  networth: string;
};

export type ProbeOverviewAddress = {
  id?: string | number;
  type?: string;
  address_type?: string;
  line1?: string;
  address_line1?: string;
  line2?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  stateName?: string;
  pincode?: string;
  gstin?: string;
};

export type ProbeOpenCharge = {
  id?: number;
  date?: string;
  holder_name?: string;
  amount?: number;
  type?: string;
};

export type ProbeReportCard = {
  id: string;
  title: string;
  summary: string;
  url: string;
};

export type GstinBusiness = {
  gstin: string;
  trade_name?: string;
  legal_name?: string;
  entity_type?: string;
  entity_status?: string;
  registration_date?: string;
  address?: string;
  industry_segments?: Array<{ industry: string; segments: string[] }>;
};

export type CapitalInformation = {
  paidUpCapital?: number | null;
  totalContributionReceived?: number | null;
  authorizedCapital?: number | null;
  totalObligationOfContribution?: number | null;
  sumOfCharges?: number | null;
};

function gstinStatusVariant(
  status?: string
): "success" | "warning" | "default" {
  const s = (status || "").toLowerCase();
  if (s === "active") return "success";
  if (s === "suspended") return "warning";
  return "default";
}

function isGstinNA(v?: string) {
  return !v || v.trim().toUpperCase() === "NA";
}

function fmtGstinDate(d?: string) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
}

const DEFAULT_CREDIT_RATINGS = [
  "AAA",
  "AA+",
  "AA",
  "AA-",
  "A+",
  "A",
  "A-",
  "BBB+",
  "BBB",
  "BBB-",
  "BB+",
  "BB",
  "BB-",
  "B+",
  "B",
  "B-",
  "C",
  "D",
];

function ReportCards({ reports }: { reports: ProbeReportCard[] }) {
  if (!reports.length) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        No reports available at the moment.
      </p>
    );
  }
  return (
    <ul className="flex gap-3 overflow-x-auto pb-2">
      {reports.map((report) => (
        <li key={report.id} className="w-64 shrink-0 rounded-lg border p-4">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-muted">
            <FileText className="h-4 w-4 text-muted-foreground" />
          </span>
          <p className="mt-3 text-sm font-semibold leading-snug">{report.title}</p>
          <p className="mt-2 line-clamp-3 text-xs text-muted-foreground">
            {report.summary}
          </p>
          <a
            href={report.url}
            target="_blank"
            rel="noreferrer"
            className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
          >
            View Report <ExternalLink className="h-3 w-3" />
          </a>
        </li>
      ))}
    </ul>
  );
}

function isCompanyListed(status?: string | null): boolean {
  return (status || "").trim().toLowerCase() === "listed";
}

export type CompanyOverviewProps = {
  domain?: string | null;
  companyName?: string | null;
  logoUrl?: string | null;
  apolloOrganization?: unknown;
  useTestData?: boolean;
  /** Used for market snapshot when company status is listed. */
  customerId?: string | null;
  /** Optional non-Apollo ticker/exchange (normalized before API call). */
  marketTicker?: string | null;
  marketExchange?: string | null;
  /** Probe / MCA fields from customer JSON */
  cin?: string | null;
  status?: string | null;
  leiNumber?: string | null;
  dataLastUpdated?: string | null;
  addresses?: ProbeOverviewAddress[];
  openCharges?: ProbeOpenCharge[];
  showGstinRegistrations?: boolean;
  gstinBusinesses?: GstinBusiness[];
  showCapitalInformation?: boolean;
  capitalInformation?: CapitalInformation | null;
  aboutCompany?: string;
  businessModel?: string;
  canGenerateCompanySummary?: boolean;
  isGeneratingCompanySummary?: boolean;
  isSavingCompanySummary?: boolean;
  onAboutCompanyChange?: (value: string) => void;
  onBusinessModelChange?: (value: string) => void;
  onCompanySummaryBlur?: () => void;
  onGenerateCompanySummary?: () => void;
  /** Triggers `customers/getIndustrySummary` from the Sector Outlook AI control. */
  onGenerateIndustrySummary?: () => void | Promise<void>;
  isGeneratingIndustrySummary?: boolean;
  canGenerateIndustrySummary?: boolean;
  industryOutlook?: ProbeReportCard[];
  /** Raw or normalized sector outlook payload (metrics, drivers, risks). */
  sectorOutlook?: SectorIndustryOutlookData | Record<string, unknown> | null;
  sectorName?: string | null;
  /** Only show Industry Outlook after comprehensive API (`isComprehensiveApiFetched`) is true. */
  isComprehensiveApiFetched?: boolean;
  news?: ProbeReportCard[];
  creditRatings?: string[];
  creditEditable?: Partial<CreditEditable>;
  onCreditSave?: (next: CreditEditable) => void | Promise<void>;
  onManageAddresses?: () => void;
  formatCurrency?: (amount: number) => string;
  financialScoreSlot?: React.ReactNode;
  financialPerformanceSlot?: React.ReactNode;
  getAddressTypeLabel?: (type?: string) => string;
  getStateName?: (stateId?: string | number) => string;
};

export function CompanyOverview({
  domain,
  companyName,
  logoUrl,
  apolloOrganization,
  useTestData = true,
  customerId,
  marketTicker,
  marketExchange,
  cin,
  status,
  leiNumber,
  dataLastUpdated,
  addresses = [],
  openCharges = [],
  showGstinRegistrations = false,
  gstinBusinesses = [],
  showCapitalInformation = false,
  capitalInformation = null,
  aboutCompany = "",
  businessModel = "",
  canGenerateCompanySummary = false,
  isGeneratingCompanySummary = false,
  isSavingCompanySummary = false,
  onAboutCompanyChange,
  onBusinessModelChange,
  onCompanySummaryBlur,
  onGenerateCompanySummary,
  onGenerateIndustrySummary,
  isGeneratingIndustrySummary = false,
  canGenerateIndustrySummary = true,
  industryOutlook = [],
  sectorOutlook = null,
  sectorName = null,
  isComprehensiveApiFetched = false,
  news = [],
  creditRatings = DEFAULT_CREDIT_RATINGS,
  creditEditable,
  onCreditSave,
  onManageAddresses,
  formatCurrency = (n) =>
    `₹${Number(n || 0).toLocaleString("en-IN")}`,
  financialScoreSlot,
  financialPerformanceSlot,
  getAddressTypeLabel = (type) => type || "Address",
  getStateName = (state) => String(state ?? ""),
}: CompanyOverviewProps) {
  const resolvedDomain = (domain || "").trim().toLowerCase() || "company.com";
  const resolvedSectorOutlook = useMemo(() => {
    const fallbackSources = industryOutlook.map((report) => ({
      id: report.id,
      title: report.title,
      url: report.url,
    }));

    let normalized = normalizeSectorIndustryOutlook(sectorOutlook, {
      sectorName,
      fallbackSources,
    });

    // Test sample fills in the card (incl. Growth Drivers / Key Risks) when live outlook is missing.
    if (!normalized && useTestData) {
      normalized = normalizeSectorIndustryOutlook(
        SECTOR_INDUSTRY_OUTLOOK_TEST_PAYLOAD,
        {
          sectorName: sectorName,
          fallbackSources,
        }
      );
    }

    if (!normalized) {
      normalized = normalizeSectorIndustryOutlook(industryOutlook, {
        sectorName,
        fallbackSources,
      });
    }

    if (!normalized) return null;

    return {
      ...normalized,
      sectorName: normalized.sectorName || sectorName || null,
      sources: normalized.sources?.length ? normalized.sources : fallbackSources,
    };
  }, [sectorOutlook, sectorName, industryOutlook, useTestData]);
  const showIndustryOutlook =
    Boolean(isComprehensiveApiFetched) &&
    (!!resolvedSectorOutlook || industryOutlook.length > 0);
  const [company, setCompany] = useState<CompanyProfile | null>(null);
  const [usingApiApollo, setUsingApiApollo] = useState(false);
  const [showAllAddresses, setShowAllAddresses] = useState(false);
  const [selectedBiz, setSelectedBiz] = useState<GstinBusiness | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSavingCredit, setIsSavingCredit] = useState(false);
  const [editable, setEditable] = useState<CreditEditable>({
    brandName: creditEditable?.brandName || "",
    creditRating: creditEditable?.creditRating || "",
    creditRatingAgency: creditEditable?.creditRatingAgency || "",
    turnoverYear: creditEditable?.turnoverYear || "",
    turnover: creditEditable?.turnover || "",
    networthYear: creditEditable?.networthYear || "",
    networth: creditEditable?.networth || "",
  });
  const [draft, setDraft] = useState<CreditEditable>(editable);

  useEffect(() => {
    const next: CreditEditable = {
      brandName: creditEditable?.brandName || "",
      creditRating: creditEditable?.creditRating || "",
      creditRatingAgency: creditEditable?.creditRatingAgency || "",
      turnoverYear: creditEditable?.turnoverYear || "",
      turnover: creditEditable?.turnover || "",
      networthYear: creditEditable?.networthYear || "",
      networth: creditEditable?.networth || "",
    };
    setEditable(next);
    if (!isEditing) setDraft(next);
  }, [creditEditable, isEditing]);

  useEffect(() => {
    const fromApi = apolloOrganization
      ? normalizeApolloOrganization(apolloOrganization, resolvedDomain)
      : null;
    if (fromApi) {
      setCompany(fromApi);
      setUsingApiApollo(true);
      return;
    }
    if (useTestData) {
      setCompany(
        normalizeApolloOrganization(APOLLO_COMPANY_TEST_PAYLOAD, resolvedDomain)
      );
      setUsingApiApollo(false);
      return;
    }
    setCompany(null);
    setUsingApiApollo(false);
  }, [apolloOrganization, resolvedDomain, useTestData]);

  const location = useMemo(() => {
    if (!company) return null;
    return (
      [company.city, company.state, company.country].filter(Boolean).join(", ") ||
      company.rawAddress
    );
  }, [company]);

  const visibleAddresses = showAllAddresses
    ? addresses
    : addresses.slice(0, 2);
  const openChargesTotal = openCharges.reduce(
    (sum, charge) => sum + Number(charge.amount || 0),
    0
  );

  const currentYear = new Date().getFullYear();
  const fundingEvents = company?.fundingEvents ?? [];
  const recentFunding = fundingEvents.filter((event) => {
    const year = event.date ? new Date(event.date).getFullYear() : null;
    return year !== null && currentYear - year <= 3;
  });
  const apolloNews = fundingEvents.filter((event) => event.newsUrl);
  const currentHeadcount = company?.estimatedNumEmployees ?? null;
  const pastHeadcount = (rate: number | null) => {
    const growth = asGrowthRate(rate);
    if (
      currentHeadcount === null ||
      growth === null ||
      1 + growth <= 0
    ) {
      return null;
    }
    return Math.round(currentHeadcount / (1 + growth));
  };
  const headcountTrend =
    currentHeadcount === null
      ? []
      : (
          [
            {
              label: "24 mo ago",
              employees: pastHeadcount(company?.headcountGrowthTwentyFourMonth ?? null),
            },
            {
              label: "12 mo ago",
              employees: pastHeadcount(company?.headcountGrowthTwelveMonth ?? null),
            },
            {
              label: "6 mo ago",
              employees: pastHeadcount(company?.headcountGrowthSixMonth ?? null),
            },
            { label: "Now", employees: currentHeadcount },
          ] as { label: string; employees: number | null }[]
        ).filter(
          (point): point is { label: string; employees: number } =>
            point.employees !== null
        );
  const departmentalEntries = Object.entries(
    company?.departmentalHeadCount ?? {}
  ).sort((a, b) => b[1] - a[1]);
  const linkEntries = company
    ? (Object.entries(company.links).filter(([, url]) => Boolean(url)) as [
        string,
        string,
      ][])
    : [];

  const setField = (key: keyof CreditEditable, value: string) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    if (isSavingCredit) return;
    setIsSavingCredit(true);
    try {
      await onCreditSave?.(draft);
      setEditable(draft);
      setIsEditing(false);
    } finally {
      setIsSavingCredit(false);
    }
  };

  const cancel = () => {
    if (isSavingCredit) return;
    setDraft(editable);
    setIsEditing(false);
  };

  const displayName =
    companyName?.trim() || company?.name || resolvedDomain;
  // const displayLogo = logoUrl || company?.logoUrl;

  return (
    <section className="space-y-5 rounded-lg border bg-white p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <Landmark className="h-4 w-4" /> Company Overview
        </h3>
        {/* <div className="flex items-center gap-2">
          <Badge variant="secondary">Apollo</Badge>
          <Badge variant="outline">Probe / MCA</Badge>
          {!usingApiApollo && useTestData && (
            <Badge variant="outline">Test Apollo</Badge>
          )}
        </div> */}
      </div>

      {(company?.shortDescription || company?.seoDescription) && (
        <div className="flex flex-col gap-4 rounded-lg border p-4 sm:flex-row sm:items-start">
          {/* {displayLogo ? (
            <img
              src={displayLogo}
              alt={`${displayName} logo`}
              loading="lazy"
              className="h-16 w-16 shrink-0 rounded-lg border bg-background object-contain p-1.5"
            />
          ) : (
            <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg border bg-muted">
              <Building2 className="h-6 w-6 text-muted-foreground" />
            </span>
          )} */}
          <div className="min-w-0 flex-1">
            {/* <h4 className="text-lg font-semibold">{displayName}</h4> */}
            <p className="mt-1 text-sm text-muted-foreground">
              {company?.shortDescription || company?.seoDescription}
            </p>
            {/* <div className="mt-3 flex flex-wrap gap-2"> */}
             
              {/* {company?.foundedYear && (
                <Badge variant="outline">Founded {company.foundedYear}</Badge>
              )} */}
              {/* {location && <Badge variant="outline">{location}</Badge>} */}
              {/* {resolvedDomain && resolvedDomain !== "company.com" && (
                <Badge variant="outline">{resolvedDomain}</Badge>
              )} */}
            {/* </div> */}
          </div>
        </div>
      )}

      <div className="rounded-lg border p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-semibold text-muted-foreground">
            Key Company Details
          </p>
          {/* <p className="text-xs text-muted-foreground">Apollo + Probe / MCA</p> */}
        </div>
        <dl className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <ReadField
            label="Annual Revenue"
            value={money(company?.annualRevenue ?? null, company?.annualRevenuePrinted ?? null)}
          />
          <ReadField
            label="Total Funding"
            value={money(company?.totalFunding ?? null, company?.totalFundingPrinted ?? null)}
          />
          <ReadField label="CIN" value={cin || NA} />
          <ReadField label="Status" value={status || NA} />
          <ReadField
            label="Latest Funding Stage"
            value={
              company?.latestFundingStage
                ? titleCase(company.latestFundingStage)
                : NA
            }
          />
          <ReadField
            label="Latest Funding Round"
            value={dateLabel(company?.latestFundingRoundDate ?? null) ?? NA}
          />
          <ReadField
            label="Employees"
            value={
              company?.estimatedNumEmployees != null
                ? company.estimatedNumEmployees.toLocaleString("en-IN")
                : NA
            }
          />
          <ReadField label="Phone" value={company?.phone ?? NA} />
          <ReadField
            label="Trading Symbol"
            value={company?.publiclyTradedSymbol ?? NA}
          />
          <ReadField
            label="Stock Exchange"
            value={company?.publiclyTradedExchange ?? NA}
          />
          <ReadField label="Data Last Updated" value={dataLastUpdated || NA} />
          <ReadField label="LEI Number" value={leiNumber || NA} />
          <ReadField
            label="Industry"
            value={company?.industry ? titleCase(company.industry) : NA}
          />
        </dl>
{/* 
        {!!company?.keywords?.length && (
          <div className="mt-4 flex flex-wrap gap-1.5 border-t pt-3">
            {company.keywords.slice(0, 12).map((keyword) => (
              <Badge key={keyword} variant="outline" className="text-[10px]">
                {titleCase(keyword)}
              </Badge>
            ))}
          </div>
        )} */}

        <div className="mt-5 border-t pt-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
              {/* <Pencil className="h-3.5 w-3.5" /> Credit Inputs */}
            </p>
            {!isEditing ? (
              <Button size="sm" variant="outline" onClick={() => setIsEditing(true)}>
                <Pencil className="h-4 w-4 mr-1" /> Edit
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={cancel}
                  disabled={isSavingCredit}
                >
                  <X className="h-4 w-4 mr-1" /> Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={() => void save()}
                  disabled={isSavingCredit}
                >
                  {isSavingCredit ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-1" />
                  )}
                  {isSavingCredit ? "Saving…" : "Save"}
                </Button>
              </div>
            )}
          </div>

          {isEditing ? (
            <>
              <div className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <Label htmlFor="probe-brand">Brand Name</Label>
                  <Input
                    id="probe-brand"
                    className="mt-1.5"
                    placeholder="Enter brand name"
                    value={draft.brandName}
                    disabled={isSavingCredit}
                    onChange={(e) => setField("brandName", e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="probe-rating">Credit Rating</Label>
                  <Select
                    value={draft.creditRating || undefined}
                    onValueChange={(value) => setField("creditRating", value)}
                    disabled={isSavingCredit}
                  >
                    <SelectTrigger id="probe-rating" className="mt-1.5 w-full">
                      <SelectValue placeholder="Select credit rating" />
                    </SelectTrigger>
                    <SelectContent>
                      {creditRatings.map((rating) => (
                        <SelectItem key={rating} value={rating}>
                          {rating}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="probe-agency">Credit Rating Agency</Label>
                  <Input
                    id="probe-agency"
                    className="mt-1.5"
                    placeholder="Enter rating agency"
                    value={draft.creditRatingAgency}
                    disabled={isSavingCredit}
                    onChange={(e) =>
                      setField("creditRatingAgency", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div className="rounded-md border p-3">
                  <p className="text-xs font-semibold">Turnover</p>
                  <div className="mt-2 grid gap-3 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="probe-turnover-year">Financial year</Label>
                      <Input
                        id="probe-turnover-year"
                        className="mt-1.5"
                        placeholder="e.g. 2024-25"
                        value={draft.turnoverYear}
                        disabled={isSavingCredit}
                        onChange={(e) => setField("turnoverYear", e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="probe-turnover">Turnover</Label>
                      <Input
                        id="probe-turnover"
                        className="mt-1.5"
                        placeholder="e.g. ₹530 Cr"
                        value={draft.turnover}
                        disabled={isSavingCredit}
                        onChange={(e) => setField("turnover", e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className="rounded-md border p-3">
                  <p className="text-xs font-semibold">Networth</p>
                  <div className="mt-2 grid gap-3 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="probe-networth-year">Financial year</Label>
                      <Input
                        id="probe-networth-year"
                        className="mt-1.5"
                        placeholder="e.g. 2024-25"
                        value={draft.networthYear}
                        disabled={isSavingCredit}
                        onChange={(e) => setField("networthYear", e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="probe-networth">Net worth</Label>
                      <Input
                        id="probe-networth"
                        className="mt-1.5"
                        placeholder="e.g. ₹108 Cr"
                        value={draft.networth}
                        disabled={isSavingCredit}
                        onChange={(e) => setField("networth", e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <dl className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
              <ReadField
                label="Brand Name"
                value={editable.brandName || NA}
                hideIfUnavailable={false}
              />
              <ReadField
                label="Credit Rating"
                value={editable.creditRating || NA}
                hideIfUnavailable={false}
              />
              <ReadField
                label="Rating Agency"
                value={editable.creditRatingAgency || NA}
                hideIfUnavailable={false}
              />
              <ReadField
                label={`Turnover${editable.turnoverYear ? ` (${editable.turnoverYear})` : ""}`}
                value={editable.turnover || NA}
                hideIfUnavailable={false}
              />
              <ReadField
                label={`Net Worth${editable.networthYear ? ` (${editable.networthYear})` : ""}`}
                value={editable.networth || NA}
                hideIfUnavailable={false}
              />
            </dl>
          )}
        </div>
      </div>

      <div className="rounded-lg border p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
            <MapPin className="h-3.5 w-3.5" /> Addresses ({addresses.length})
          </p>
          {onManageAddresses && (
            <Button size="sm" variant="outline" onClick={onManageAddresses}>
              Manage
            </Button>
          )}
        </div>
        {visibleAddresses.length ? (
          <ul className="mt-3 grid gap-3 sm:grid-cols-2">
            {visibleAddresses.map((address, index) => (
              <li
                key={String(address.id ?? index)}
                className="rounded-md border p-3"
              >
                <Badge variant="outline">
                  {getAddressTypeLabel(
                    address.type || address.address_type
                  )}
                </Badge>
                <p className="mt-2 text-sm">
                  {address.line1 || address.address_line1 || NA}
                  {(address.line2 || address.address_line2)
                    ? `, ${address.line2 || address.address_line2}`
                    : ""}
                </p>
                <p className="text-sm text-muted-foreground">
                  {address.city || NA},{" "}
                  {address.stateName ||
                    getStateName(address.state) ||
                    address.state ||
                    NA}{" "}
                  — {address.pincode || NA}
                </p>
                {address.gstin && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    GSTIN: {address.gstin}
                  </p>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 text-sm text-muted-foreground">No addresses added yet.</p>
        )}
        {addresses.length > 2 && (
          <Button
            className="mt-3"
            size="sm"
            variant="ghost"
            onClick={() => setShowAllAddresses((current) => !current)}
          >
            {showAllAddresses
              ? "Show less"
              : `View ${addresses.length - 2} more`}
            <ChevronDown
              className={`h-4 w-4 ml-1 ${showAllAddresses ? "rotate-180" : ""}`}
            />
          </Button>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-lg border p-4">
          <div className="flex items-center justify-between gap-2">
            <p className="flex items-center gap-1.5 text-xs font-semibold">
              <Building2 className="h-3.5 w-3.5" /> About Company
            </p>
            {canGenerateCompanySummary && (
              <button
                type="button"
                className="flex items-center gap-1 text-purple-600 text-xs cursor-pointer hover:text-purple-700"
                onClick={onGenerateCompanySummary}
                disabled={isGeneratingCompanySummary}
              >
                {isGeneratingCompanySummary ? (
                  <div style={AI_SPINNER_STYLE} />
                ) : (
                  <>
                    <Sparkles className="h-3.5 w-3.5" />
                    <span>Generate With AI</span>
                  </>
                )}
              </button>
            )}
          </div>
          <Textarea
            value={aboutCompany}
            onChange={(e) => onAboutCompanyChange?.(e.target.value)}
            onBlur={onCompanySummaryBlur}
            placeholder="Enter company description..."
            className="mt-3 min-h-[160px] resize-none text-sm"
          />
          {isSavingCompanySummary && (
            <p className="mt-2 text-xs text-muted-foreground">Saving changes...</p>
          )}
        </div>
        <div className="rounded-lg border p-4">
          <div className="flex items-center justify-between gap-2">
            <p className="flex items-center gap-1.5 text-xs font-semibold">
              <Landmark className="h-3.5 w-3.5" /> Business Model
            </p>
            {canGenerateCompanySummary && (
              <button
                type="button"
                className="flex items-center gap-1 text-purple-600 text-xs cursor-pointer hover:text-purple-700"
                onClick={onGenerateCompanySummary}
                disabled={isGeneratingCompanySummary}
              >
                {isGeneratingCompanySummary ? (
                  <div style={AI_SPINNER_STYLE} />
                ) : (
                  <>
                    <Sparkles className="h-3.5 w-3.5" />
                    <span>Generate With AI</span>
                  </>
                )}
              </button>
            )}
          </div>
          <Textarea
            value={businessModel}
            onChange={(e) => onBusinessModelChange?.(e.target.value)}
            onBlur={onCompanySummaryBlur}
            placeholder="Enter business model description..."
            className="mt-3 min-h-[160px] resize-none text-sm"
          />
          {isSavingCompanySummary && (
            <p className="mt-2 text-xs text-muted-foreground">Saving changes...</p>
          )}
        </div>
      </div>

      {isCompanyListed(status) && customerId && companyName?.trim() && (
        <MarketData
          key={customerId}
          ticker={marketTicker}
          exchange={marketExchange}
          companyName={companyName.trim()}
          customerId={customerId}
          useTestData={useTestData}
        />
      )}

      {financialScoreSlot}

      {financialPerformanceSlot && (
        <div className="rounded-lg border p-4">
          <p className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <Building2 className="h-4 w-4" /> Financial Performance
          </p>
          {financialPerformanceSlot}
        </div>
      )}

     

      <Accordion
        type="multiple"
        defaultValue={
          company
            ? [
                "funding",
                "employees",
                ...(showIndustryOutlook || news.length > 0
                  ? ["outlook"]
                  : []),
                ...(showGstinRegistrations ? ["gstin"] : []),
                ...(!showGstinRegistrations && showCapitalInformation
                  ? ["capital"]
                  : []),
                ...(!showGstinRegistrations ? ["charges"] : []),
              ]
            : [
                ...(showIndustryOutlook || news.length > 0
                  ? ["outlook"]
                  : []),
                ...(showGstinRegistrations ? ["gstin"] : ["capital", "charges"]),
              ]
        }
        className="border-t"
      >
        {company && (
          <>
            <AccordionItem value="funding">
              <AccordionTrigger>
                <span className="flex items-center gap-2">
                  <Landmark className="h-4 w-4" /> Funding ({fundingEvents.length})
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <p className="mb-3 text-xs text-muted-foreground">
                  Last 3 years: {recentFunding.length} round
                  {recentFunding.length === 1 ? "" : "s"}
                </p>
                {fundingEvents.length ? (
                  <ul className="divide-y">
                    {fundingEvents.map((event, index) => (
                      <li
                        key={event.id ?? index}
                        className="flex flex-wrap items-start gap-3 py-3 text-sm"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="font-medium">
                            {event.type ? titleCase(event.type) : "Funding round"}
                            {event.amount
                              ? ` • ${event.currency ?? "$"}${event.amount}`
                              : ""}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {event.investors ?? "Investors not listed"}
                          </p>
                        </div>
                        <div className="shrink-0 text-right">
                          <p className="text-xs text-muted-foreground">
                            {dateLabel(event.date) ?? "Date not listed"}
                          </p>
                          {event.newsUrl && (
                            <a
                              href={event.newsUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs text-primary hover:underline"
                            >
                              News <ExternalLink className="inline h-3 w-3" />
                            </a>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No funding history available for this company.
                  </p>
                )}
              </AccordionContent>
            </AccordionItem>

            {/* <AccordionItem value="apollo-news">
              <AccordionTrigger>
                <span className="flex items-center gap-2">
                  <Newspaper className="h-4 w-4" /> Funding News (
                  {apolloNews.length})
                </span>
              </AccordionTrigger>
              <AccordionContent>
                {apolloNews.length ? (
                  <ul className="space-y-3">
                    {apolloNews.map((event, index) => (
                      <li key={event.id ?? index} className="text-sm">
                        <a
                          href={event.newsUrl!}
                          target="_blank"
                          rel="noreferrer"
                          className="font-medium text-primary hover:underline"
                        >
                          {event.type ? titleCase(event.type) : "Funding"}{" "}
                          announcement{" "}
                          <ExternalLink className="inline h-3 w-3" />
                        </a>
                        <p className="text-xs text-muted-foreground">
                          {dateLabel(event.date) ?? "Date not listed"}
                          {event.amount
                            ? ` • ${event.currency ?? "$"}${event.amount}`
                            : ""}
                        </p>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No funding-related news links available for this company.
                  </p>
                )}
              </AccordionContent>
            </AccordionItem> */}

            <AccordionItem value="employees">
              <AccordionTrigger>
                <span className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" /> Employee trend
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4">
                  <p className="text-sm">
                    Current estimate:{" "}
                    <span className="font-medium">
                      {currentHeadcount?.toLocaleString("en-IN") ?? NA}
                    </span>
                  </p>
                  {headcountTrend.length > 1 ? (
                    <>
                      <div className="h-56 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart
                            data={headcountTrend}
                            margin={{ top: 8, right: 24, bottom: 4, left: 8 }}
                          >
                            <defs>
                              <linearGradient
                                id="headcountLine"
                                x1="0"
                                y1="0"
                                x2="1"
                                y2="0"
                              >
                                <stop
                                  offset="0%"
                                  stopColor="#8200DB"
                                  stopOpacity={0.5}
                                />
                                <stop
                                  offset="100%"
                                  stopColor="#8200DB"
                                  stopOpacity={1}
                                />
                              </linearGradient>
                            </defs>
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke="#e5e7eb"
                              vertical={false}
                            />
                            <XAxis
                              dataKey="label"
                              tickLine={false}
                              axisLine={false}
                              tick={{ fontSize: 12, fill: "#6b7280" }}
                              padding={{ left: 12, right: 12 }}
                            />
                            <YAxis
                              width={72}
                              tickLine={false}
                              axisLine={false}
                              tick={{ fontSize: 12, fill: "#6b7280" }}
                              tickFormatter={(value: number) =>
                                value.toLocaleString("en-IN")
                              }
                            />
                            <ChartTooltip
                              cursor={{ stroke: "#e5e7eb" }}
                              contentStyle={{
                                borderRadius: 8,
                                border: "1px solid #e5e7eb",
                                background: "#fff",
                                fontSize: 12,
                              }}
                              formatter={(value: number) => [
                                value.toLocaleString("en-IN"),
                                "Employees",
                              ]}
                            />
                            <Line
                              type="monotone"
                              dataKey="employees"
                              stroke="url(#headcountLine)"
                              strokeWidth={2.5}
                              dot={{
                                r: 4,
                                strokeWidth: 0,
                                fill: "#8200DB",
                              }}
                              activeDot={{ r: 6 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Past headcount is estimated from 6, 12 and 24-month
                        growth rates applied to the current figure.
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No headcount growth history available for this company.
                    </p>
                  )}

                  {/* {departmentalEntries.length > 0 && (
                    <div>
                      <p className="mb-2 text-xs font-semibold text-muted-foreground">
                        Departmental headcount
                      </p>
                      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                        {departmentalEntries.map(([dept, count]) => (
                          <div key={dept}>
                            <dt className="text-xs text-muted-foreground">
                              {titleCase(dept)}
                            </dt>
                            <dd className="mt-1 text-sm font-medium">
                              {count.toLocaleString("en-IN")}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  )} */}
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="structure">
              <AccordionTrigger>
                <span className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" /> Corporate structure &amp;
                  subsidiaries ({company.suborganizations.length})
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <StructureField
                    label="Immediate parent"
                    value={
                      company.ownedByOrganization?.name ??
                      company.ownedByOrganization?.id ??
                      "No parent recorded"
                    }
                  />
                  <StructureField
                    label="Ultimate parent"
                    value={
                      company.ultimateParentOrganization?.name ??
                      company.ultimateParentOrganization?.id ??
                      "No parent recorded"
                    }
                  />
                  {/* <StructureField
                    label="Parent chain"
                    value={
                      company.ownedByChain.length
                        ? company.ownedByChain.join(" → ")
                        : "Not available"
                    }
                  /> */}
                </dl>
                <div className="mt-4">
                  {company.suborganizations.length ? (
                    <ul className="divide-y">
                      {company.suborganizations.map((sub, index) => (
                        <li
                          key={sub.id ?? index}
                          className="flex flex-wrap items-start gap-3 py-3 text-sm"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="font-medium">
                              {sub.name ?? "Subsidiary"}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {sub.industries.length
                                ? sub.industries.map(titleCase).join(", ")
                                : "Industry not listed"}
                            </p>
                          </div>
                          <p className="shrink-0 text-xs text-muted-foreground">
                            {[
                              sub.estimatedNumEmployees
                                ? `${sub.estimatedNumEmployees.toLocaleString("en-IN")} employees`
                                : null,
                              sub.country,
                            ]
                              .filter(Boolean)
                              .join(" • ") || "Details not listed"}
                          </p>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No direct subsidiaries returned.
                    </p>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
{/* 
            <AccordionItem value="links">
              <AccordionTrigger>
                <span className="flex items-center gap-2">
                  <ExternalLink className="h-4 w-4" /> Links
                </span>
              </AccordionTrigger>
              <AccordionContent>
                {linkEntries.length ? (
                  <ul className="flex flex-wrap gap-2">
                    {linkEntries.map(([label, url]) => (
                      <li key={label}>
                        <a
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm text-primary hover:bg-accent"
                        >
                          {titleCase(label)}{" "}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No public profile links returned.
                  </p>
                )}

                {!!company.technologies.length && (
                  <div className="mt-4 border-t pt-3">
                    <p className="mb-2 text-xs font-semibold text-muted-foreground">
                      Technologies
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {company.technologies.slice(0, 24).map((tech) => (
                        <Badge
                          key={`${tech.name}-${tech.category ?? ""}`}
                          variant="secondary"
                          className="text-[10px]"
                        >
                          {tech.name}
                          {tech.category ? ` · ${tech.category}` : ""}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </AccordionContent>
            </AccordionItem> */}
          </>
        )}

        {(showIndustryOutlook || news.length > 0) && (
          <AccordionItem value="outlook">
            <AccordionTrigger>
              <span className="flex items-center gap-2">
                <FileText className="h-4 w-4" /> Industry Outlook &amp; News
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <Tabs
                defaultValue={showIndustryOutlook ? "outlook" : "news"}
              >
                <TabsList>
                  {showIndustryOutlook && (
                    <TabsTrigger value="outlook">Industry Outlook</TabsTrigger>
                  )}
                  {news.length > 0 && (
                    <TabsTrigger value="news">News</TabsTrigger>
                  )}
                </TabsList>
                {showIndustryOutlook && (
                  <TabsContent value="outlook" className="mt-3">
                    {resolvedSectorOutlook ? (
                      <SectorIndustryOutlookCard
                        data={resolvedSectorOutlook}
                        onGenerateAI={onGenerateIndustrySummary}
                        isGeneratingAI={isGeneratingIndustrySummary}
                        canGenerateAI={canGenerateIndustrySummary}
                      />
                    ) : (
                      <ReportCards reports={industryOutlook} />
                    )}
                  </TabsContent>
                )}
                {news.length > 0 && (
                  <TabsContent value="news" className="mt-3">
                    <ReportCards reports={news} />
                  </TabsContent>
                )}
              </Tabs>
            </AccordionContent>
          </AccordionItem>
        )}

        {showGstinRegistrations && (
          <AccordionItem value="gstin">
            <AccordionTrigger>
              <span className="flex items-center gap-2">
                <FileText className="h-4 w-4" /> GSTIN Registrations (
                {gstinBusinesses.length})
              </span>
            </AccordionTrigger>
            <AccordionContent>
              {gstinBusinesses.length ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>GSTIN</TableHead>
                        <TableHead>State</TableHead>
                        <TableHead>Trade Name</TableHead>
                        <TableHead>Entity Type</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Reg. Date</TableHead>
                        <TableHead>Industries</TableHead>
                        <TableHead>Address</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {gstinBusinesses.map((b) => {
                        const stateCode = getStateCodeFromGSTIN(b.gstin);
                        const industries = Array.from(
                          new Set(
                            (b.industry_segments || []).map((i) => i.industry)
                          )
                        );
                        const visibleInd = industries.slice(0, 2);
                        const extra = industries.length - visibleInd.length;
                        return (
                          <TableRow key={b.gstin} className="hover:bg-muted/30">
                            <TableCell>
                              <div className="flex items-center gap-1.5">
                                <span className="font-mono text-xs font-medium">
                                  {b.gstin}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    navigator.clipboard.writeText(b.gstin);
                                    toast.success("GSTIN copied");
                                  }}
                                  className="text-muted-foreground hover:text-primary"
                                >
                                  <Copy className="h-3 w-3" />
                                </button>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1.5">
                                <Chip
                                  variant="info"
                                  className="px-1.5 py-0 text-[10px] font-mono"
                                >
                                  {STATE_SHORT_MAP[stateCode] || stateCode}
                                </Chip>
                                <span className="text-xs">
                                  {STATE_NAME_MAP[stateCode] || "—"}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">
                              {isGstinNA(b.trade_name) ? (
                                <span className="text-muted-foreground">—</span>
                              ) : (
                                b.trade_name
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">
                                {b.entity_type}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Chip
                                variant={gstinStatusVariant(b.entity_status)}
                                className="px-2 py-0.5 text-[10px]"
                              >
                                {b.entity_status}
                              </Chip>
                            </TableCell>
                            <TableCell className="text-xs whitespace-nowrap">
                              {fmtGstinDate(b.registration_date)}
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {visibleInd.map((i) => (
                                  <Badge
                                    key={i}
                                    variant="secondary"
                                    className="text-[10px]"
                                  >
                                    {i}
                                  </Badge>
                                ))}
                                {extra > 0 && (
                                  <UiTooltipProvider>
                                    <UiTooltip>
                                      <UiTooltipTrigger asChild>
                                        <Badge
                                          variant="outline"
                                          className="text-[10px] cursor-help"
                                        >
                                          +{extra}
                                        </Badge>
                                      </UiTooltipTrigger>
                                      <UiTooltipContent>
                                        <div className="text-xs space-y-0.5">
                                          {industries.slice(2).map((i) => (
                                            <div key={i}>{i}</div>
                                          ))}
                                        </div>
                                      </UiTooltipContent>
                                    </UiTooltip>
                                  </UiTooltipProvider>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <UiTooltipProvider>
                                <UiTooltip>
                                  <UiTooltipTrigger asChild>
                                    <div className="flex items-start gap-1.5 max-w-[200px] cursor-help">
                                      <MapPin className="h-3 w-3 text-muted-foreground mt-0.5 flex-shrink-0" />
                                      <span className="text-xs text-muted-foreground truncate">
                                        {b.address}
                                      </span>
                                    </div>
                                  </UiTooltipTrigger>
                                  <UiTooltipContent className="max-w-sm">
                                    <p className="text-xs">{b.address}</p>
                                  </UiTooltipContent>
                                </UiTooltip>
                              </UiTooltipProvider>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setSelectedBiz(b)}
                              >
                                <Eye className="h-3.5 w-3.5 mr-1" />
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No GSTIN registrations found.
                </p>
              )}
            </AccordionContent>
          </AccordionItem>
        )}

        {!showGstinRegistrations && showCapitalInformation && (
          <AccordionItem value="capital">
            <AccordionTrigger>
              <span className="flex items-center gap-2">
                <Landmark className="h-4 w-4" /> Capital Information
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {(capitalInformation?.paidUpCapital ||
                  capitalInformation?.totalContributionReceived) && (
                  <div>
                    <p className="text-xs text-muted-foreground">
                      {capitalInformation?.paidUpCapital
                        ? "Paid Up Capital"
                        : "Total Contribution Received"}
                    </p>
                    <p className="mt-1 text-sm font-medium">
                      {formatCurrency(
                        Number(
                          capitalInformation?.paidUpCapital
                            ? capitalInformation.paidUpCapital
                            : capitalInformation?.totalContributionReceived || 0
                        )
                      )}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-muted-foreground">
                    {capitalInformation?.authorizedCapital
                      ? "Authorized Capital"
                      : "Total Obligation of Contribution"}
                  </p>
                  <p className="mt-1 text-sm font-medium">
                    {formatCurrency(
                      Number(
                        capitalInformation?.authorizedCapital
                          ? capitalInformation.authorizedCapital
                          : capitalInformation?.totalObligationOfContribution ||
                              0
                      )
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Sum of Charges</p>
                  <p className="mt-1 text-sm font-medium">
                    {formatCurrency(
                      Number(capitalInformation?.sumOfCharges || 0)
                    )}
                  </p>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {!showGstinRegistrations && (
          <AccordionItem value="charges">
            <AccordionTrigger>
              <span className="flex items-center gap-2">
                <Landmark className="h-4 w-4" /> Open Charges ({openCharges.length})
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <p className="mb-3 text-sm text-muted-foreground">
                Total: {formatCurrency(openChargesTotal)}
              </p>
              {openCharges.length ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Holder</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Type</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {openCharges.map((charge, index) => (
                        <TableRow key={String(charge.id ?? index)}>
                          <TableCell className="whitespace-nowrap">
                            {charge.date
                              ? new Date(charge.date).toLocaleDateString("en-IN")
                              : NA}
                          </TableCell>
                          <TableCell>{charge.holder_name || NA}</TableCell>
                          <TableCell className="whitespace-nowrap">
                            {formatCurrency(Number(charge.amount || 0))}
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{charge.type || NA}</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No open charges recorded.
                </p>
              )}
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>

      <Sheet
        open={!!selectedBiz}
        onOpenChange={(open) => !open && setSelectedBiz(null)}
      >
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          {selectedBiz && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary" />
                  GSTIN Details
                </SheetTitle>
              </SheetHeader>
              <div className="mt-6 space-y-5">
                <div className="space-y-1">
                  <div className="text-xs uppercase text-muted-foreground tracking-wide">
                    GSTIN
                  </div>
                  <div className="font-mono font-medium">{selectedBiz.gstin}</div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs uppercase text-muted-foreground tracking-wide">
                      Status
                    </div>
                    <Chip
                      variant={gstinStatusVariant(selectedBiz.entity_status)}
                      className="mt-1 px-2 py-0.5 text-[11px]"
                    >
                      {selectedBiz.entity_status}
                    </Chip>
                  </div>
                  <div>
                    <div className="text-xs uppercase text-muted-foreground tracking-wide">
                      Entity Type
                    </div>
                    <div className="text-sm font-medium mt-1">
                      {selectedBiz.entity_type}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase text-muted-foreground tracking-wide">
                      Registered On
                    </div>
                    <div className="text-sm font-medium mt-1">
                      {fmtGstinDate(selectedBiz.registration_date)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase text-muted-foreground tracking-wide">
                      State
                    </div>
                    <div className="text-sm font-medium mt-1">
                      {STATE_NAME_MAP[
                        getStateCodeFromGSTIN(selectedBiz.gstin)
                      ] || "—"}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase text-muted-foreground tracking-wide mb-1">
                    Legal Name
                  </div>
                  <div className="text-sm">{selectedBiz.legal_name}</div>
                </div>
                {!isGstinNA(selectedBiz.trade_name) && (
                  <div>
                    <div className="text-xs uppercase text-muted-foreground tracking-wide mb-1">
                      Trade Name
                    </div>
                    <div className="text-sm">{selectedBiz.trade_name}</div>
                  </div>
                )}
                <div>
                  <div className="text-xs uppercase text-muted-foreground tracking-wide mb-1">
                    Principal Address
                  </div>
                  <div className="text-sm flex items-start gap-1.5">
                    <MapPin className="h-3.5 w-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <span>{selectedBiz.address}</span>
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase text-muted-foreground tracking-wide mb-2">
                    Industries &amp; Segments
                  </div>
                  <div className="space-y-2">
                    {(selectedBiz.industry_segments || []).map(
                      ({ industry, segments }) => (
                        <div
                          key={industry}
                          className="rounded-md border bg-muted/20 p-2.5"
                        >
                          <div className="text-sm font-medium mb-1.5">
                            {industry}
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {segments.map((s) => (
                              <Chip
                                key={s}
                                variant="default"
                                className="text-[10px] px-1.5 py-0.5"
                              >
                                {s}
                              </Chip>
                            ))}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </section>
  );
}
